import { ApolloQueryResult } from '@apollo/client';
import {
  Text,
  useToast,
  HStack,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  AlertIcon,
  Alert as ChakraAlert,
  Spacer,
  Checkbox,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { Navigate } from 'react-router';

import { ValleyBankPortfolioReceivableResponseTapeStats } from '@finley-technologies/valley-bank-utils';

import {
  GetValleyBankAssignmentQueueQueryGQLType,
  useAddToValleyBankAssignmentQueueMutation,
  useGetUserQuery,
  useGetValleyBankAssignmentDeviationQuery,
  useGetValleyBankAssignmentQueueQuery,
  ValleyBankAddToAssignmentQueueGQLType,
  ValleyBankCollateralToLoanAccountMappingGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { ActionableAlert } from './ActionableAlert';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Alert } from '~/packages/common-frontend/components/Alert';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { LoanAccountSummaryLink } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoanAccountsOverviewTemplate } from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsOverviewTemplate';
import { LoanAccountsTable } from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsTable';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { useUserRoleTypePermissionsContext } from '~/valleyBank/domains/page/hooks/useUserRoleTypePermissions';
import {
  LoanAndCollateralAccountsStats,
  LoanAndCollateralAccountsStatsComponentProps,
} from '~/valleyBank/domains/page/LoanAccountsAndCollateralAccountsStats';

const toComponentProps = (
  portfolioStats: ValleyBankPortfolioReceivableResponseTapeStats,
): LoanAndCollateralAccountsStatsComponentProps => {
  const {
    unassignedTotalMarketValue,
    unassignedTotalLendableValue,
    unassignedEffectiveAdvanceRate,
    assignedTotalMarketValue,
    assignedTotalLendableValue,
    assignedEffectiveAdvanceRate,
    totalOutstandingBalance,
    totalMarginAvailability,
    totalMarginAvailabilityRate,
    loanAccountsCount,
    underMarginStatusCount,
    nearMarginStatusCount,
    excessMarginStatusCount,
  } = portfolioStats;

  return {
    loanStats: {
      excessMarginStatusCount,
      loanAccountsCount,
      nearMarginStatusCount,
      totalMarginAvailability,
      totalMarginAvailabilityRate,
      totalOutstandingBalance,
      underMarginStatusCount,
    },
    collateralStats: {
      assigned: {
        totalPreAllocationLendableValue: assignedTotalLendableValue,
        totalMarketValue: assignedTotalMarketValue,
        effectiveAdvanceRate: assignedEffectiveAdvanceRate,
      },
      unassigned: {
        totalPreAllocationLendableValue: unassignedTotalLendableValue,
        totalMarketValue: unassignedTotalMarketValue,
        effectiveAdvanceRate: unassignedEffectiveAdvanceRate,
      },
    },
  };
};

function notUndefined<T>(value: T | undefined): value is T {
  if (value === undefined) return false;
  return true;
}

type UnassignModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  collateralAccountsToUnassign: ValleyBankCollateralToLoanAccountMappingGQLType[];
  alertText: string;
  userId: string;
  refetchAssignmentQueue: () => Promise<
    ApolloQueryResult<GetValleyBankAssignmentQueueQueryGQLType>
  >;
  isMissingLoanAccount?: boolean;
};
const UnassignModal = ({
  isOpen,
  onCancel,
  collateralAccountsToUnassign,
  alertText,
  userId,
  refetchAssignmentQueue,
  isMissingLoanAccount,
}: UnassignModalProps) => {
  const toast = useToast();

  const [
    addToValleyBankAssignmentQueueMutation,
  ] = useAddToValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      // mutation returns back list of errors
      // surface any errors here
      if (data.valleyBankAddToAssignmentsQueue.length > 0) {
        if (!toast.isActive(errorToastId)) {
          toast({
            ...errorToastConfig,
            title: 'Errors on adding unassignment to queue',
            description: data.valleyBankAddToAssignmentsQueue.join('; '),
          });
        }
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully add unassignment to queue.',
        });
      }
    },
  });

  const collateralAccsToLoanAccsMap: Map<
    string,
    ValleyBankAddToAssignmentQueueGQLType[]
  > = new Map();

  for (const pair of collateralAccountsToUnassign) {
    const existingLoanAccs = collateralAccsToLoanAccsMap.get(
      pair.collateralAccountNum,
    );

    const assignment: ValleyBankAddToAssignmentQueueGQLType = {
      collateralAccountNumber: pair.collateralAccountNum,
      loanAccountNumber: pair.loanAccountNum,
      collateralAccountType: pair.collateralAccountType,
      action: 'unassign',
      userId,
    };

    if (existingLoanAccs) {
      collateralAccsToLoanAccsMap.set(pair.collateralAccountNum, [
        ...existingLoanAccs,
        assignment,
      ]);
    } else {
      collateralAccsToLoanAccsMap.set(pair.collateralAccountNum, [assignment]);
    }
  }

  const [selectedCollateralAccounts, setSelectedCollateralAccounts] = useState<
    Set<string>
  >(new Set());

  return (
    <Modal isOpen={isOpen} onClose={() => onCancel()} size="3xl">
      <ModalOverlay />
      <ModalContent padding={2}>
        <ModalHeader>Unassign Collateral</ModalHeader>
        <ModalCloseButton />
        <ModalBody mb="25px">
          <ChakraAlert
            status="info"
            mb="20px"
            fontSize="16px"
            fontWeight="semibold"
          >
            <AlertIcon />
            {alertText}
          </ChakraAlert>
          <Table variant="simple" mb="25px" borderWidth="1px">
            <Thead>
              <Tr>
                <Th color="black" fontSize="16px" fontWeight={500}>
                  Collateral
                </Th>
                <Th color="black" fontSize="16px" fontWeight={500}>
                  Assigned to Loan Account
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {collateralAccsToLoanAccsMap.size === 0 && (
                <Tr>
                  <Td colSpan={2}>
                    <Text>There is nothing to unassign.</Text>
                  </Td>
                </Tr>
              )}
              {[...collateralAccsToLoanAccsMap].map(
                ([collatAccNum, loanAccs]) => {
                  return (
                    <Tr key={`${collatAccNum}`}>
                      <Td color={colors.black[70]}>
                        <Checkbox
                          isChecked={selectedCollateralAccounts.has(
                            collatAccNum,
                          )}
                          onChange={(_e) => {
                            setSelectedCollateralAccounts((curr) => {
                              const copy = _.cloneDeep(curr);
                              if (copy.has(collatAccNum)) {
                                // delete
                                copy.delete(collatAccNum);
                              } else {
                                // add
                                copy.add(collatAccNum);
                              }
                              return copy;
                            });
                          }}
                        >
                          {collatAccNum}
                        </Checkbox>
                      </Td>
                      <Td>
                        <HStack spacing="15px" color={colors.black[70]}>
                          {isMissingLoanAccount
                            ? loanAccs.map((la) => {
                                // if loan account is missing, we cannot show a link that pushes to the detail summary view
                                return (
                                  <Text
                                    key={la.loanAccountNumber}
                                    color={colors.black[70]}
                                    fontSize="16px"
                                    fontWeight={500}
                                  >
                                    {la.loanAccountNumber}
                                  </Text>
                                );
                              })
                            : loanAccs.map((la) => {
                                return (
                                  <LoanAccountSummaryLink
                                    key={la.loanAccountNumber}
                                    loanAccountId={la.loanAccountNumber}
                                  >
                                    {la.loanAccountNumber}
                                  </LoanAccountSummaryLink>
                                );
                              })}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                },
              )}
            </Tbody>
          </Table>
          <Text mt="25px" mb="25px" color={colors.black[70]} fontSize="15px">
            Go to the Assignments page to view unassignments.
          </Text>

          <HStack spacing="20px">
            <Spacer />
            <Button
              py="4px"
              px="20px"
              color={colors.aurora[400]}
              backgroundColor="white"
              borderColor={colors.aurora[400]}
              borderWidth="2px"
              onClick={onCancel}
              _focus={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
            <Button
              py="4px"
              px="20px"
              color="white"
              backgroundColor={colors.aurora[400]}
              onClick={async () => {
                const unassignmentsToAddToQueue = [
                  ...selectedCollateralAccounts,
                ]
                  .flatMap((num) => collateralAccsToLoanAccsMap.get(num))
                  .filter(notUndefined);

                if (unassignmentsToAddToQueue.length > 0) {
                  await addToValleyBankAssignmentQueueMutation({
                    variables: {
                      records: unassignmentsToAddToQueue,
                    },
                  });
                  await refetchAssignmentQueue();
                }
                onCancel();
              }}
              _focus={{ boxShadow: 'none' }}
            >
              Unassign
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const UnassignedAlert = () => {
  const toast = useToast();
  const [
    isUnassignNoLongerPledgedModalOpen,
    setIsUnassignNoLongerPledgedModalOpen,
  ] = useState<boolean>(false);

  const [
    isUnassignMissingLAModalOpen,
    setIsUnassignMissingLAModalOpen,
  ] = useState<boolean>(false);

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const {
    data: assignmentDeviationData,
  } = useGetValleyBankAssignmentDeviationQuery({
    fetchPolicy: 'network-only',
  });

  const {
    data: assignmentQueueData,
    refetch: refetchAssignmentQueue,
  } = useGetValleyBankAssignmentQueueQuery({
    fetchPolicy: 'network-only',
  });

  // if no data or both unassign fields are empty, return here
  if (
    !userData ||
    !userData.user ||
    !assignmentQueueData ||
    !assignmentDeviationData ||
    (assignmentDeviationData.valleyBankAssignmentDeviations
      .collateralAccountsAssignedToNonExistentLoanAccountNum.length === 0 &&
      assignmentDeviationData.valleyBankAssignmentDeviations
        .collateralAccountsNoLongerPledged.length === 0)
  ) {
    return <></>;
  }

  const allCollateralAccountsNoLongerPledged =
    assignmentDeviationData.valleyBankAssignmentDeviations
      .collateralAccountsNoLongerPledged;

  const collateralAccountsNoLongerPledgedNotInQueue: ValleyBankCollateralToLoanAccountMappingGQLType[] = [];
  for (const collateralAcc of allCollateralAccountsNoLongerPledged) {
    const duplicateRecord = assignmentQueueData.valleyBankAssignmentQueue.find(
      (q) =>
        q.collateralAccountNumber === collateralAcc.collateralAccountNum &&
        q.loanAccountNumber === collateralAcc.loanAccountNum &&
        q.collateralAccountType === collateralAcc.collateralAccountType &&
        q.action === 'unassign',
    );

    if (!duplicateRecord) {
      collateralAccountsNoLongerPledgedNotInQueue.push(collateralAcc);
    }
  }

  const allCollateralAccountsAssignedToNonExistentLoanAccountNum =
    assignmentDeviationData.valleyBankAssignmentDeviations
      .collateralAccountsAssignedToNonExistentLoanAccountNum;

  const collateralAccountsAssignedToNonExistentLoanAccountNumNotInQueue: ValleyBankCollateralToLoanAccountMappingGQLType[] = [];
  for (const collateralAcc of allCollateralAccountsAssignedToNonExistentLoanAccountNum) {
    const duplicateRecord = assignmentQueueData.valleyBankAssignmentQueue.find(
      (q) =>
        q.collateralAccountNumber === collateralAcc.collateralAccountNum &&
        q.loanAccountNumber === collateralAcc.loanAccountNum &&
        q.collateralAccountType === collateralAcc.collateralAccountType &&
        q.action === 'unassign',
    );

    if (!duplicateRecord) {
      collateralAccountsAssignedToNonExistentLoanAccountNumNotInQueue.push(
        collateralAcc,
      );
    }
  }

  const noLongerPledgedAlertText = `Finley has ${collateralAccountsNoLongerPledgedNotInQueue.length} assigned collateral accounts that are marked with a pledged_indicator of 'N' by Valley Bank or are missing in today's data tapes.`;
  const missingLoanAccountAlertText = `Finley has ${collateralAccountsAssignedToNonExistentLoanAccountNumNotInQueue.length} assigned to loan accounts that are missing in today's data.`;

  const userId = userData.user.id;

  return (
    <>
      {/* Unassigned for Valley, assigned for Finley */}
      <UnassignModal
        isOpen={isUnassignNoLongerPledgedModalOpen}
        onCancel={() => {
          setIsUnassignNoLongerPledgedModalOpen(false);
        }}
        collateralAccountsToUnassign={
          collateralAccountsNoLongerPledgedNotInQueue
        }
        alertText={noLongerPledgedAlertText}
        userId={userId}
        refetchAssignmentQueue={refetchAssignmentQueue}
      />
      {collateralAccountsNoLongerPledgedNotInQueue.length > 0 && (
        <ActionableAlert
          actionElement={
            <Button
              variant="link"
              color="blue.400"
              fontWeight="semibold"
              fontSize="16px"
              onClick={() => {
                setIsUnassignNoLongerPledgedModalOpen(true);
              }}
              _focus={{ boxShadow: 'none' }}
            >
              View
            </Button>
          }
          status="info"
          mb="20px"
        >
          <Text ml={3} fontWeight="semibold" fontSize="16px">
            {noLongerPledgedAlertText}
          </Text>
        </ActionableAlert>
      )}

      {/* Assigned for Finley but loan Accounts are missing in today's data drop */}
      <UnassignModal
        isOpen={isUnassignMissingLAModalOpen}
        onCancel={() => {
          setIsUnassignMissingLAModalOpen(false);
        }}
        collateralAccountsToUnassign={
          collateralAccountsAssignedToNonExistentLoanAccountNumNotInQueue
        }
        alertText={missingLoanAccountAlertText}
        userId={userId}
        refetchAssignmentQueue={refetchAssignmentQueue}
        isMissingLoanAccount
      />
      {collateralAccountsAssignedToNonExistentLoanAccountNumNotInQueue.length >
        0 && (
        <ActionableAlert
          actionElement={
            <Button
              variant="link"
              color="blue.400"
              fontWeight="semibold"
              fontSize="16px"
              onClick={() => {
                setIsUnassignMissingLAModalOpen(true);
              }}
              _focus={{ boxShadow: 'none' }}
            >
              View
            </Button>
          }
          status="info"
          mb="20px"
        >
          <Text ml={3} fontWeight="semibold" fontSize="16px">
            {missingLoanAccountAlertText}
          </Text>
        </ActionableAlert>
      )}
    </>
  );
};

const RouteBase = () => {
  const { permissions } = useUserRoleTypePermissionsContext();
  const {
    isPreview,
    dashboardMode,
    portfolioStats,
    isPortfolioStatsLoading,
  } = useDataMetadataContext();

  const canDisplayUnassignAlerts =
    dashboardMode === 'current' &&
    permissions.assignmentQueueRead &&
    permissions.assignmentQueueEdit;
  return (
    <>
      {canDisplayUnassignAlerts && <UnassignedAlert />}
      <LoanAccountsOverviewTemplate selectedTab="summary">
        {!isPortfolioStatsLoading && !portfolioStats && (
          <Alert status="info">
            <Text>{`${
              isPreview ? 'Preview portfolio' : 'Portfolio'
            } snapshot unavailable`}</Text>
          </Alert>
        )}
        {!isPortfolioStatsLoading && portfolioStats && (
          <LoanAndCollateralAccountsStats
            {...toComponentProps(portfolioStats)}
          />
        )}
        <LoanAccountsTable />
      </LoanAccountsOverviewTemplate>
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="./" />;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
